<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  address: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  process: { type: Object, default: null },
});

const { t } = useI18n();
const processData = computed(() => props.process || {});
const propertyAddress = computed(() => props.address || {});
const locationLine = computed(() =>
  [propertyAddress.value.bairro, propertyAddress.value.cidade]
    .filter(Boolean)
    .join(' - ')
);
const streetLine = computed(() => {
  const street = [
    propertyAddress.value.logradouro,
    propertyAddress.value.numero,
  ]
    .filter(Boolean)
    .join(', ');
  const complement = [
    propertyAddress.value.unidade,
    propertyAddress.value.complemento,
  ]
    .filter(Boolean)
    .join(' / ');

  return [street, complement].filter(Boolean).join(' / ');
});
const team = computed(() =>
  [
    {
      detail: '',
      key: 'post-sale',
      label: t('REAL_ESTATE_FINANCE.DETAIL.CONTEXT_POST_SALE'),
      person: processData.value.responsible,
    },
    {
      detail: processData.value.loja?.name || '',
      key: 'manager',
      label: t('REAL_ESTATE_FINANCE.DETAIL.CONTEXT_MANAGER'),
      person: processData.value.manager,
    },
  ].filter(member => member.person?.name)
);
const initials = name =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
</script>

<template>
  <section
    data-testid="finance-installment-context-header"
    class="rounded-lais border border-lais-border-soft bg-white px-4 py-5 shadow-lais-card sm:px-6"
  >
    <div
      class="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between"
    >
      <div class="min-w-0">
        <div v-if="loading" class="space-y-3 animate-pulse">
          <div class="h-4 w-40 rounded bg-slate-100" />
          <div class="h-4 w-80 max-w-full rounded bg-slate-100" />
        </div>
        <div v-else class="space-y-2">
          <p class="flex items-center gap-2 text-sm text-lais-text-muted">
            <span
              class="i-lucide-map-pin size-4 shrink-0 text-lais-primary"
              aria-hidden="true"
            />
            <span>
              {{
                locationLine ||
                t('REAL_ESTATE_FINANCE.DETAIL.CONTEXT_ADDRESS_UNAVAILABLE')
              }}
            </span>
          </p>
          <p class="pl-6 text-base font-semibold text-lais-text">
            {{
              streetLine ||
              t('REAL_ESTATE_FINANCE.DETAIL.CONTEXT_ADDRESS_UNAVAILABLE')
            }}
          </p>
        </div>
      </div>
      <div
        v-if="loading"
        class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:min-w-[30rem]"
      >
        <div v-for="index in 2" :key="index" class="flex items-center gap-3">
          <div
            class="size-12 shrink-0 rounded-full bg-slate-100 animate-pulse"
          />
          <div class="flex-1 space-y-2 animate-pulse">
            <div class="h-3 w-16 rounded bg-slate-100" />
            <div class="h-4 w-28 rounded bg-slate-100" />
          </div>
        </div>
      </div>
      <div
        v-else-if="team.length"
        class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:min-w-[30rem]"
      >
        <article
          v-for="member in team"
          :key="member.key"
          data-context-person
          class="flex min-w-0 items-center gap-3"
        >
          <span
            class="flex size-12 shrink-0 items-center justify-center rounded-full bg-lais-primary/10 text-sm font-bold text-lais-primary"
            aria-hidden="true"
          >
            {{ initials(member.person.name) }}
          </span>
          <div class="min-w-0">
            <p
              class="text-xs font-semibold uppercase tracking-wide text-lais-text-muted"
            >
              {{ member.label }}
            </p>
            <p class="truncate font-semibold text-lais-text">
              {{ member.person.name }}
            </p>
            <p
              v-if="member.detail"
              class="truncate text-sm text-lais-text-muted"
            >
              {{ member.detail }}
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
